import { getApiConfig, type Stage } from '../config/apiConfig';
import {
  cognitoIdentityProvider,
  type IdentityProvider,
} from '../auth/identityProvider';
import {
  cognitoTokenProvider,
  type TokenProvider,
} from '../auth/tokenProvider';
import { ApiClient } from './http/apiClient';
import { MetaInfoStore } from './stores/metaInfoStore';
import { MetaDataStore } from './stores/metaDataStore';
import { DocumentStore } from './stores/documentStore';
import { InferenceApi } from './inference/inferenceApi';
import { KnowledgeApi } from './knowledge/knowledgeApi';
import { KnowledgeBaseApi } from './knowledgeBase/knowledgeBaseApi';
import { OrchestratorApi } from './orchestrator/orchestratorApi';
import { TeamOnboardingApi } from './teamOnboarding/teamOnboardingApi';
import { TenantAccessApi } from './tenantAccess/tenantAccessApi';

/**
 * EngineeringVectorApi — the single public entry point (facade) into the API
 * layer. Constructs the ApiClient and wires the capability clients.
 *
 * NOTE: People is intentionally OMITTED (deferred pending PAPI onboarding).
 * Inference and Knowledge are included. Adding People later = add a PeopleApi
 * class + one facade field; no other plumbing changes.
 *
 * Pure TS — no React. React components reach this only through the useApi hook.
 */

export interface EngineeringVectorApiOptions {
  stage?: Stage;
  identityProvider?: IdentityProvider;
  tokenProvider?: TokenProvider;
}

export class EngineeringVectorApi {
  readonly identityProvider: IdentityProvider;
  readonly metaInfoStore: MetaInfoStore;
  readonly metaDataStore: MetaDataStore;
  readonly inference: InferenceApi;
  readonly knowledge: KnowledgeApi;
  readonly knowledgeBase: KnowledgeBaseApi;
  readonly orchestrator: OrchestratorApi;
  readonly teamOnboarding: TeamOnboardingApi;
  readonly tenantAccess: TenantAccessApi;

  private readonly apiClient: ApiClient;

  constructor(options: EngineeringVectorApiOptions = {}) {
    const { baseUrl } = getApiConfig(options.stage);
    const identityProvider =
      options.identityProvider ?? cognitoIdentityProvider;
    const tokenProvider = options.tokenProvider ?? cognitoTokenProvider;

    this.identityProvider = identityProvider;
    this.apiClient = new ApiClient({ baseUrl, tokenProvider });

    this.metaInfoStore = new MetaInfoStore(this.apiClient, identityProvider);
    this.metaDataStore = new MetaDataStore(this.apiClient, identityProvider);
    this.inference = new InferenceApi(this.apiClient, identityProvider);
    this.knowledge = new KnowledgeApi(this.apiClient, identityProvider);
    this.knowledgeBase = new KnowledgeBaseApi(this.apiClient, identityProvider);
    this.orchestrator = new OrchestratorApi(this.apiClient, identityProvider);
    this.teamOnboarding = new TeamOnboardingApi(
      this.apiClient,
      identityProvider,
    );
    this.tenantAccess = new TenantAccessApi(this.apiClient, identityProvider);
  }

  /**
   * Returns a reusable typed DocumentStore for a collection. Each feature calls
   * this with its own document type, e.g.:
   *   const templates = api.documents<Template>('templates');
   */
  documents<T>(collection: string): DocumentStore<T> {
    return new DocumentStore<T>(
      this.metaInfoStore,
      this.metaDataStore,
      collection,
    );
  }
}

// Re-exports for convenient consumption from features.
export { ApiClient } from './http/apiClient';
export { ApiError } from './http/apiError';
export { DocumentStore } from './stores/documentStore';
export { MetaInfoStore } from './stores/metaInfoStore';
export type { ListMetaInfoQuery } from './stores/metaInfoStore';
export { MetaDataStore } from './stores/metaDataStore';
export { InferenceApi } from './inference/inferenceApi';
export { KnowledgeApi } from './knowledge/knowledgeApi';
export { KnowledgeBaseApi } from './knowledgeBase/knowledgeBaseApi';
export { OrchestratorApi } from './orchestrator/orchestratorApi';
export type {
  DescribeExecutionArgs,
  ExecutionDescription,
} from './orchestrator/orchestratorApi';
export { TeamOnboardingApi } from './teamOnboarding/teamOnboardingApi';
export { TenantAccessApi, DEFAULT_SCOPE } from './tenantAccess/tenantAccessApi';
export type {
  GrantTenantAccessArgs,
  RevokeTenantAccessArgs,
  TenantShare,
} from './tenantAccess/tenantAccessApi';
export type {
  ValidateAccountArgs,
  StartTeamOnboardingArgs,
  GetStatusArgs,
  CheckTeamAccessArgs,
  ListOwnerBindlesArgs,
  OnboardingAccount,
  OnboardedTeam,
  OwnedBindle,
} from './teamOnboarding/teamOnboardingApi';
export type {
  KbSource,
  KbCitation,
  KbSummary,
  KbListResult,
  RetrieveAndGenerateArgs,
  RetrieveArgs,
  ListKnowledgeBasesArgs,
  StartIngestionArgs,
  UploadDocumentArgs,
  StartKnowledgeBaseProvisioningArgs,
  KnowledgeBaseProvisioningResult,
  TenantKnowledgeBase,
  TenantKbListResult,
  RegisterKnowledgeBaseArgs,
} from './knowledgeBase/knowledgeBaseApi';
export * from './model/metaInfo';
export * from './model/types';
